/**
 * Pure world-space transform math for one `.WLD` placement — shared by the
 * `/assets` World viewer's 3D pane and `/fly`'s region loader, kept free of
 * three.js so every case is a plain-arithmetic unit test.
 *
 * Two conventions compose here, both already established elsewhere in this
 * app rather than invented fresh:
 * - The model-frame → world-frame fix (`flight/world.ts`'s doc comment): a
 *   baked model's own vertices are X-right, Y-longitudinal(nose+), Z-up;
 *   the engine/three.js frame is Y-up, forward -Z. A -90° pitch about X
 *   converts one to the other: `(x, y, z) -> (x, z, -y)`. `modelFrameToWorld`
 *   below is that same conversion, exposed as pure math so it's tested
 *   independently of whichever three.js call site applies it (per-mesh
 *   `rotation.x`, or a static geometry pre-rotation shared by every instance
 *   of one object — the latter is what `three/world-instancer.ts` uses).
 * - The placement's own position/heading. `.WLD` `x`/`y` are the *same*
 *   world frame the model vertices are expressed in, so they have to reach
 *   three.js through the *same* map: `(X, Y, Z) -> (X, Z, -Y)`. A placement
 *   is a ground point (`Z = 0`), so that is `x -> +X`, **`y -> -Z`**, with
 *   `rotationDegrees` (BAM16, world-terrain.md §1.2) becoming a `+yaw` about
 *   three's Y. Which world axis is "north" is still UNKNOWN and this file
 *   still doesn't guess — but the *sign* is not a free choice, and getting
 *   it wrong is the bug this note exists to prevent recurring.
 *
 * **Why `y -> -Z` and not `+Z` (fixed 2026-08-19, user report "the rivers
 * are way off").** Mapping `y -> +Z` while geometry goes through
 * `modelFrameToWorld` makes the position map `[[1,0,0],[0,0,1],[0,1,0]]`,
 * determinant **-1** — a reflection. Composed with a proper-rotation
 * geometry map it mirrors every placed object against its own placement
 * grid: positions land in a north/south-mirrored layout while each object's
 * shape and heading stay unmirrored, so separately-placed road, river and
 * trench pieces stop meeting end to end. Symmetric town blocks hide it;
 * a polyline network cannot.
 *
 * Measured, not just argued. `MAPn.DAT` (the briefing maps) stores its
 * vector art — front line, towns, aerodromes — in the *same* coordinates as
 * `.WLD`, which gives an external heading reference: the elongated
 * front-line trench sections (`TERR2-82`/`TERR3-82`/`TERR4-137`/`TERR5-78`)
 * are placed along that polyline, so their long axis has to follow its local
 * tangent. Over all 61 such placements in the four front-line regions the
 * mean |long axis - tangent| error is **16.5° with `y -> -Z`** against
 * **35.6° with `y -> +Z`** (45° would be chance), and the discriminating
 * cases land dead on: `TERR4-137` at 314°/315°/340° against tangents of
 * 134°/134°/160°. See docs/formats/world-terrain.md §2.5.
 */

import type { Vec3 } from "@red-opus/engine";
import { wldUnitsToMetres } from "./wld-units.ts";

/** Model-frame `(x, y, z)` -> world-frame `(x, z, -y)` — the app's one -90° X convention. */
export function modelFrameToWorld(x: number, y: number, z: number): Vec3 {
  return { x, y: z, z: -y };
}

export interface PlacementLike {
  readonly x: number;
  readonly y: number;
  readonly rotationDegrees: number;
}

/**
 * The point every placement is recentred against, in metres but still on the
 * **`.WLD` axes** — `x` is a `.WLD` `x` and `z` is a `.WLD` `y`, both run
 * through `wldUnitsToMetres`. Recentring happens before the `y -> -Z` flip,
 * which is what lets a caller build one straight from a raw placement-space
 * point (`flight/fly-region.ts`'s start aerodrome, `assets/world-scene.ts`'s
 * bounds centre) without knowing about the flip at all. Ground Y is always 0
 * — the flat-plane verdict, world-terrain.md §5.
 */
export interface PlacementOrigin {
  readonly x: number;
  readonly z: number;
}

export const ORIGIN_ZERO: PlacementOrigin = { x: 0, z: 0 };

export interface PlacementWorldTransform {
  /** World-space metres, ground plane at y = 0. */
  readonly position: Vec3;
  /** Yaw about world Y, radians. */
  readonly yawRadians: number;
}

/**
 * One placement's world position + yaw, in metres, recentred by `origin`
 * (also in metres — callers convert an origin's own raw `.WLD` units with
 * `wldUnitsToMetres` first, e.g. the chosen start aerodrome's projected
 * position for `/fly`, or the region's own bounds centre for the `/assets`
 * 3D pane).
 */
export function placementWorldTransform(placement: PlacementLike, origin: PlacementOrigin = ORIGIN_ZERO): PlacementWorldTransform {
  return {
    position: {
      x: wldUnitsToMetres(placement.x) - origin.x,
      y: 0,
      // `-` because `.WLD` `y` is the world axis `modelFrameToWorld` sends to
      // three's `-Z`; see this module's doc comment for why the sign matters.
      z: -(wldUnitsToMetres(placement.y) - origin.z),
    },
    yawRadians: (placement.rotationDegrees * Math.PI) / 180,
  };
}
